import React from 'react';
import { RadioGroup } from '@headlessui/react';
import tw from 'twin.macro';
import Img from '@teetsh/app/src/components/Img';
import { labelDesign } from '@teetsh/app/src/components/Form/Label';

interface Option<T> {
  value: T;
  title: string;
  description: any;
  img?: string;
}

interface Props<T> {
  options: Option<T>[];
  value: T;
  onChange: (v: T) => void;
  label?: string;
  radioPosition?: 'top' | 'center';
}

export default function ListWithDescription<T = string>(props: Props<T>) {
  const { options, value, onChange, label, radioPosition } = props;
  return (
    <RadioGroup<any, T | null> value={value} onChange={onChange}>
      {label && (
        <RadioGroup.Label css={[labelDesign, tw`mb-2`]}>
          {label}
        </RadioGroup.Label>
      )}
      <div tw="bg-white rounded-md -space-y-px">
        {options.map((option, idx) => (
          <RadioGroup.Option
            key={`${option.value}`}
            value={option.value}
            aria-label={option.title}
            css={[
              tw`relative border p-4 flex cursor-pointer focus:outline-none items-center`,
              tw`border border-neutral-quiet hover:border-brand-moderate`,
              idx === 0 && tw`rounded-tl-md rounded-tr-md`,
              idx === options.length - 1 && tw`rounded-bl-md rounded-br-md`,
              value === option.value && tw`bg-brand-moderate z-10`,
              radioPosition === 'top' && tw`items-start`,
            ]}
          >
            {({ checked }) => (
              <>
                <span
                  css={[
                    checked && tw`bg-brand-intense border-transparent`,
                    !checked && tw`bg-white border-neutral-quiet`,
                    tw`h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0`,
                  ]}
                  aria-hidden="true"
                >
                  <span tw="rounded-full bg-white w-1.5 h-1.5" />
                </span>
                <div tw="ml-3 flex flex-col">
                  <RadioGroup.Label
                    as="span"
                    css={[
                      checked && tw`text-brand-intense`,
                      !checked && tw`text-neutral-intense`,
                      tw`font-medium`,
                    ]}
                  >
                    {option.title}
                  </RadioGroup.Label>
                  <RadioGroup.Description
                    css={[
                      tw`text-sm`,
                      checked && tw`text-brand-moderate`,
                      !checked && tw`text-neutral-quiet`,
                    ]}
                    as="span"
                  >
                    {option.description}
                  </RadioGroup.Description>
                </div>
                {option.img && (
                  <RadioGroup.Description
                    as="div"
                    css={[
                      checked && tw`text-brand-moderate`,
                      !checked && tw`text-neutral-quiet`,
                      tw`pl-1 text-sm ml-auto md:pl-0 md:text-right`,
                    ]}
                  >
                    <Img s3Path={option.img} width={150} />
                  </RadioGroup.Description>
                )}
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
}
